import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormContainer } from '#components/FormContainer';
import { contactQuestionTypes, fieldQuestionTypes } from '#constants/questionTypes';
import { ThemeProvider } from '#contexts/ThemeContext';
import { LoadingDiv } from '#layouts/main-layout/LoadingDiv';
import { MainLayout } from '#layouts/main-layout/MainLayout';
import { EOutcomeType } from '#models/outcome';
import { EFieldType, EQuestionType } from '#models/questionnaire';
import { FieldQuestionLayout } from '#modules/FieldQuestionLayout';
import { LeadGenerationLayout } from '#modules/LeadGeneration/LeadGenerationLayout';
import { QuestionLayout } from '#modules/QuestionLayout';
import { getOutcomePublic, getQuestionnaireDetailsBySlug } from '#services/questionnaire';
import { EStorageKeys, storageClear, storageSet } from '#services/storage';
import { generateId } from '#utils/generate-id';
import { useEmbed, usePreview } from '#utils/param';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { colors } from '@swyft/ui';
import { flatten, get, isEmpty, isEqual, reduce, sortBy } from 'lodash-es';
import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useCompleteQuestionnaire, useCurrentQuestionId, useGetLocationById, useQuestionnaireActiveStep, useQuestionnaireDate, useQuestionnaireDetails, useQuestionnaireState, useQuestionQuestionnaire, useSetCurrentQuestionId, useSetQuestionnaireDate, useSetQuestionQuestionnaire } from '../queries/questionnaire';
import { getQuestionnaireLevel } from '../utils/getQuestionnaireLevel';
import { DiscountParams, getParamFieldsFromUrl } from '../utils/utils';
import { BackButton } from './BackButton';
import { CookieSettingsLink } from './CookieSettingsLink';
import { ErrorNotFound } from './Error';
import { QuestionElement } from './QuestionElement';
import { QuestionnaireProgress } from './QuestionnaireProgress';
export const Questionnaire = ({ callBackParent, appImages, resetQuestionnaire = false, getDataQuestionnaire, isMapEnabled, googleKey, PTID, hasLocalStorage, currency, isWhiteLabel, defaultPicture }) => {
    const classes = useStyles();
    const params = useParams();
    const routeId = params.id ?? '';
    const routeSlug = params.slug ?? '';
    const [resolvedId, setResolvedId] = React.useState('');
    const queryClient = useQueryClient();
    // If a slug is present on the route, fetch the questionnaire by slug and resolve its GUID id.
    useQuery(['questionnaire-slug', routeSlug], () => getQuestionnaireDetailsBySlug(routeSlug), {
        enabled: !!routeSlug,
        retry: false,
        onSuccess: (data) => {
            if (data?.id) {
                setResolvedId(data.id);
                // seed the questionnaire details cache keyed by the resolved id so the subsequent id-based query
                // will use this data and avoid duplicate network requests.
                queryClient.setQueryData([data.id], data);
            }
        }
    });
    const id = routeId || resolvedId;
    const isEmbed = useEmbed();
    const isPreview = usePreview();
    const queryParams = new URLSearchParams(useLocation().search);
    const payload = {
        questionnaireId: id,
        enabled: !!hasLocalStorage
    };
    const { data: questionnaire = {}, isLoading, status } = useQuestionnaireDetails(id, Boolean(isPreview === 'true'), {
        retry: false,
        enabled: Boolean(id)
    });
    // Track the last questionnaire ID we processed to avoid calling getDataQuestionnaire multiple times
    const lastProcessedQuestionnaireIdRef = React.useRef('');
    // Call getDataQuestionnaire when questionnaire data is available
    // This runs in useEffect instead of onSuccess to handle both fresh fetches and cached data
    React.useEffect(() => {
        if (questionnaire?.id && getDataQuestionnaire && lastProcessedQuestionnaireIdRef.current !== questionnaire.id) {
            lastProcessedQuestionnaireIdRef.current = questionnaire.id;
            getDataQuestionnaire(questionnaire);
            // Store theme in local storage when available
            const theme = questionnaire?.theme || {};
            if (!isEmpty(theme) && hasLocalStorage) {
                storageSet(EStorageKeys.CURRENT_THEME, theme);
                queryClient.setQueryData([EStorageKeys.CURRENT_THEME], theme);
            }
        }
    }, [questionnaire?.id, questionnaire, getDataQuestionnaire, hasLocalStorage, queryClient]);
    const [{ data: questionnaireState = {}, isLoading: loadingQuestionnaireState }, { mutateAsync: updateQuestionnaireState }] = useQuestionnaireState(payload);
    const { data: currentQuestionId, isLoading: loadingCurrentQuestionId } = useCurrentQuestionId(payload);
    const { mutateAsync: setCurrentQuestionId } = useSetCurrentQuestionId(id);
    const { data: storageQuestions, isLoading: loadingStorageQuestions } = useQuestionQuestionnaire(payload);
    const { mutateAsync: setStorageQuestions } = useSetQuestionQuestionnaire(id);
    const { data: storageDate, isLoading: loadingStorageDate } = useQuestionnaireDate(payload);
    const { mutateAsync: setStorageDate } = useSetQuestionnaireDate(id);
    // const { data: storageOutcomeId, isLoading: loadingStorageOutcomeId } = useQuestionnaireOutcomeId(payload);
    // const { mutateAsync: setStorageOutcomeId } = useSetQuestionnaireOutcomeId(id);
    const [{ data: activeStep = 1, isLoading: loadingActiveStep }, { mutateAsync: setActiveStep }] = useQuestionnaireActiveStep(payload);
    const { mutateAsync: completeQuestionnaire } = useCompleteQuestionnaire();
    const [isShowToast, setIsShowToast] = React.useState(false);
    const { data: questionLocations, isLoading: isLoadingLocation } = useGetLocationById(id);
    const [currentQuestionIdState, setCurrentQuestionIdState] = React.useState('');
    const [questionnaireStateForm, setQuestionnaireStateForm] = React.useState({
        id: '',
        questionnaireId: id,
        answerSelectionIds: [],
        questionFieldSubmissions: {},
        fieldAnswer: {}
    });
    const [activeStepState, setActiveStepState] = React.useState(1);
    const [alreadyTrackedDefaultQuestion, setAlreadyTrackedDefaultQuestion] = React.useState(false);
    const currentQuestionIdMerge = React.useMemo(() => (hasLocalStorage ? currentQuestionId : currentQuestionIdState), [hasLocalStorage, currentQuestionId, currentQuestionIdState]);
    const setCurrentQuestionIdMerge = React.useCallback((currentId) => (hasLocalStorage ? setCurrentQuestionId(currentId) : setCurrentQuestionIdState(currentId)), [hasLocalStorage, setCurrentQuestionId, setCurrentQuestionIdState]);
    const questionnaireStateMerge = React.useMemo(() => (hasLocalStorage ? questionnaireState : questionnaireStateForm), [hasLocalStorage, questionnaireState, questionnaireStateForm]);
    const setQuestionnaireStateMerge = React.useCallback((questionnaireState) => hasLocalStorage ? updateQuestionnaireState(questionnaireState) : setQuestionnaireStateForm(questionnaireState), [hasLocalStorage, updateQuestionnaireState, setQuestionnaireStateForm]);
    const activeStepMerge = React.useMemo(() => (hasLocalStorage ? activeStep : activeStepState), [hasLocalStorage, activeStep, activeStepState]);
    const setActiveStateMerge = React.useCallback((activeStep) => (hasLocalStorage ? setActiveStep(activeStep) : setActiveStepState(activeStep)), [hasLocalStorage, setActiveStep, setActiveStepState]);
    const isLoadingData = isLoading ||
        loadingCurrentQuestionId ||
        loadingActiveStep ||
        loadingQuestionnaireState ||
        loadingStorageQuestions ||
        loadingStorageDate ||
        isLoadingLocation;
    const handleResetQuestionnaire = async () => {
        await setCurrentQuestionId('');
        await setActiveStep(1);
        await updateQuestionnaireState({
            ...questionnaireState,
            answerSelectionIds: [],
            questionFieldSubmissions: {},
            fieldAnswer: {}
        });
    };
    React.useEffect(() => {
        if (isPreview === 'true' && !isLoadingData) {
            handleResetQuestionnaire();
        }
    }, [isLoadingData]);
    React.useEffect(() => {
        if (resetQuestionnaire && !isLoadingData && activeStep > 1) {
            handleResetQuestionnaire();
        }
    }, [resetQuestionnaire]);
    const currentQuestion = React.useMemo(() => {
        return ((questionnaire.questions || []).find(({ id }) => id === (currentQuestionIdMerge || questionnaire.startQuestionId)) || {});
    }, [questionnaire, currentQuestionIdMerge]);
    // A questionnaire with no resolvable start question cannot render any content. Previously the
    // `|| {}` fallback above made this state truthy, so the page rendered an empty question with no
    // title, no answers and no error - a silent blank page. Detect it explicitly instead.
    const hasUnresolvedQuestion = React.useMemo(() => !isLoadingData && !!questionnaire.questions?.length && !currentQuestion.id, [isLoadingData, questionnaire.questions, currentQuestion.id]);
    React.useEffect(() => {
        if (!isLoadingData && questionnaire.questions && isPreview !== 'true') {
            if (currentQuestion && !alreadyTrackedDefaultQuestion) {
                trackQuestionPageView(currentQuestion);
                setAlreadyTrackedDefaultQuestion(true);
            }
        }
    }, [isLoadingData, questionnaire, currentQuestion]);
    React.useEffect(() => {
        if (!isLoadingData && questionnaire.questions && isPreview !== 'true') {
            let compareDate = false;
            const today = new Date();
            if (storageDate) {
                compareDate = today.getTime() - storageDate.getTime() > 1000 * 3600 * 24;
            }
            if ((questionnaire.questions?.length && storageQuestions && !isEqual(storageQuestions, questionnaire.questions)) ||
                compareDate) {
                handleResetQuestionnaire();
                setStorageQuestions(questionnaire.questions);
                setStorageDate(new Date());
                storageClear([`${id}-outcomeId`]);
                queryClient.invalidateQueries(`${id}-outcomeId`);
                // } else if (storageOutcomeId) {
                //   navigate(`/questionnaire-outcome/${storageOutcomeId}`);
            }
            else if (!isShowToast && activeStepMerge > 1) {
                setIsShowToast(true);
            }
        }
    }, [isLoadingData, questionnaire]);
    const trackQuestionPageView = (question) => {
        // trackPageView({
        //   pageUrl: location.href + '/Question/' + question?.id || '',
        //   pageTitle: 'Question - ' + question?.title || ''
        // });
    };
    const trackAnswerEvent = (answer, question) => {
        // trackEvent({
        //   category: 'Answer',
        //   eventAction: JSON.stringify({
        //     id: answer.id,
        //     text: answer.text,
        //     questionId: answer.questionId
        //   }),
        //   eventName: question?.title + '-' + answer.text
        // });
    };
    const trackMultiAnswerEvent = (answers, question) => {
        // trackEvent({
        //   category: 'MultiAnswer',
        //   eventAction: JSON.stringify(
        //     answers.map((answer) => {
        //       return {
        //         id: answer.id,
        //         text: answer.text,
        //         questionId: answer.questionId
        //       };
        //     })
        //   ),
        //   eventName: question?.title + '- Answer'
        // });
    };
    const trackCompletion = (completedQuestionnaireId, questionnaireId) => {
        // trackEvent({
        //   category: 'Completion',
        //   eventAction: questionnaireId,
        //   eventName: completedQuestionnaireId
        // });
    };
    React.useEffect(() => {
        if (isShowToast) {
            setTimeout(() => {
                setIsShowToast(false);
            }, 5000);
        }
    }, [isShowToast]);
    const questionOrder = React.useMemo(() => {
        if (!questionnaire?.questions || !questionnaire?.startQuestionId) {
            return {};
        }
        return getQuestionnaireLevel(questionnaire.questions, questionnaire.startQuestionId);
    }, [questionnaire.questions, questionnaire.startQuestionId]);
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentQuestion]);
    const isFieldQuestion = React.useMemo(() => fieldQuestionTypes.includes(currentQuestion?.type), [currentQuestion]);
    const total = React.useMemo(() => {
        if (!questionnaire?.questions || !currentQuestion?.id) {
            return 0;
        }
        return (getQuestionnaireLevel(questionnaire.questions, currentQuestion?.id, 'level') + activeStepMerge - 1);
    }, [questionnaire.questions, currentQuestion.id, activeStepMerge]);
    let discountCode = queryParams.get(DiscountParams.Discount_Code);
    // console.log(discountCode);
    // React.useEffect(() => {
    //   let discountCode = queryParams.get(DiscountParams.Discount_Code);
    //   if (discountCode) {
    //     var discountField = currentQuestion?.fields?.find((f) => f.type === EFieldType.Discount);
    //     if (discountField) {
    //       const questionFieldSubmissions = questionnaireState.questionFieldSubmissions;
    //       questionFieldSubmissions['discount'] = discountCode;
    //       updateQuestionnaireState({ ...questionnaireState, questionFieldSubmissions });
    //     }
    //   }
    // }, [currentQuestion]);
    // TODO : If queryParams.get(DiscountParams.Discount_Code) has value
    // Set value to questionFieldSubmissions['discount'] for submission in CompletedQuestionnaire
    const dataPolygons = React.useMemo(() => {
        if (questionLocations && questionLocations.length) {
            return reduce(questionLocations, (_dataPolygons, questionLocation) => {
                const { polygons } = questionLocation;
                const polygonsObject = JSON.parse(polygons);
                const { coordinates = [] } = polygonsObject;
                const flattenCoordinates = flatten(coordinates);
                const _polygons = [];
                if (flattenCoordinates.length) {
                    flattenCoordinates.forEach((coordinate) => {
                        const polygon = coordinate.map((item) => ({ lat: item[0], lng: item[1] }));
                        if (polygon) {
                            _polygons.push(polygon);
                        }
                    });
                }
                if (_polygons.length) {
                    _dataPolygons.push(_polygons);
                }
                return _dataPolygons;
            }, []);
        }
        return [];
    }, [questionLocations]);
    const handleNext = (answer) => {
        if (!storageQuestions && hasLocalStorage) {
            setStorageQuestions(questionnaire.questions);
        }
        if (!!hasLocalStorage) {
            setStorageDate(new Date());
        }
        const question = questionnaire.questions.find((x) => x.id === answer.questionId);
        if (!question?.multipleSelection) {
            trackAnswerEvent(answer, questionnaire.questions.find((x) => x.id === answer.questionId));
        }
        if (answer.nextQuestionId) {
            trackQuestionPageView(questionnaire.questions.find((x) => x.id === answer.nextQuestionId));
            setCurrentQuestionIdMerge(answer.nextQuestionId);
            setActiveStateMerge(questionOrder[answer.nextQuestionId]);
        }
    };
    const getPreviousAnswer = () => {
        const questions = get(questionnaire, 'questions', []);
        const answeredIds = get(questionnaireStateMerge, 'answerSelectionIds', []);
        for (const question of questions) {
            const answers = get(question, 'answers', []);
            const foundAnswer = answers.filter((ans) => ans.nextQuestionId === currentQuestionIdMerge && answeredIds.includes(ans.id));
            if (foundAnswer.length) {
                return {
                    questionId: question.id,
                    foundAnswerIds: foundAnswer.map((answer) => answer.id)
                };
            }
        }
        return {};
    };
    const handleBack = () => {
        const { questionId: previousQuestionId, foundAnswerIds = [] } = getPreviousAnswer();
        const currentAnswerIds = currentQuestion?.answers.map((answer) => answer.id) || [];
        const { fieldAnswer } = questionnaireStateMerge;
        [...foundAnswerIds, ...currentAnswerIds].forEach((answerId) => {
            if (fieldAnswer && fieldAnswer.hasOwnProperty(answerId)) {
                delete fieldAnswer[answerId];
            }
        });
        setQuestionnaireStateMerge({
            ...questionnaireStateMerge,
            answerSelectionIds: questionnaireStateMerge?.answerSelectionIds?.filter((item) => ![...foundAnswerIds, ...currentAnswerIds].includes(item)),
            fieldAnswer
        });
        if (previousQuestionId) {
            trackQuestionPageView(questionnaire.questions.find((x) => x.id === previousQuestionId));
            setCurrentQuestionIdMerge(previousQuestionId);
            setActiveStateMerge(questionOrder[previousQuestionId]);
            if (hasLocalStorage) {
                setStorageDate(new Date());
            }
        }
        return;
    };
    const handleChangeFields = (type, value, addressLocation) => {
        const questionFieldSubmissions = questionnaireStateMerge.questionFieldSubmissions;
        const { answerSelectionIds } = questionnaireStateMerge;
        if (answerSelectionIds?.length) {
            questionFieldSubmissions[type] = value;
            if (type === EFieldType.Address) {
                questionFieldSubmissions['location'] = addressLocation;
            }
            setQuestionnaireStateMerge({ ...questionnaireStateMerge, questionFieldSubmissions });
        }
    };
    const handleSubmit = async (values, helpers) => {
        try {
            if (!values.id) {
                values.id = questionnaireStateMerge.id || generateId();
            }
            if (!values.questionnaireId) {
                values.questionnaireId = id;
            }
            const questionLevel = getQuestionnaireLevel(questionnaire.questions, questionnaire.startQuestionId);
            const answerSelectionIds = [...values.answerSelectionIds];
            const answerQuestion = questionnaire.questions
                .map((question) => {
                const { answers = [] } = question;
                const foundAnswers = sortBy(answers, 'sortOrder').filter((item) => answerSelectionIds.includes(item.id));
                if (foundAnswers.length)
                    return {
                        question: question.title,
                        questionId: question.id,
                        answers: foundAnswers.map((item) => contactQuestionTypes.includes(question.type)
                            ? (values?.fieldAnswer && values?.fieldAnswer[item.id]) || ''
                            : item.text),
                        answerIds: foundAnswers.map((item) => item.id).filter((id) => id !== null && id !== undefined),
                        sortOrder: questionLevel[question.id]
                    };
                return {
                    question: question.title,
                    answers: [],
                    sortOrder: []
                };
            })
                .filter((item) => item?.answers?.length);
            const completeQuestionnaireParam = {
                id: values.id,
                questionnaireId: id,
                Submission: {
                    questionFieldSubmissions: values.questionFieldSubmissions,
                    answerSelectionIds: values.answerSelectionIds,
                    paramFields: getParamFieldsFromUrl(),
                    answerDetails: sortBy(answerQuestion, 'sortOrder')
                },
                QuestionnairePublishedAt: isPreview === 'true' ? null : questionnaire.publishedAt,
                PTID: PTID || null
            };
            const completedQuestionnaireId = await completeQuestionnaire(completeQuestionnaireParam);
            const outcome = await getOutcomePublic(completedQuestionnaireId);
            // trackLead(lead, id);
            trackCompletion(completedQuestionnaireId, id);
            if (hasLocalStorage) {
                await storageClear([`${id}-activeStep`, `${id}-current`, `${id}-state`, `${id}-questions`, `${id}-date`]);
            }
            helpers?.setStatus(true);
            if (callBackParent) {
                callBackParent(values.id, outcome);
            }
            if ((isEmbed === 'true' || isPreview === 'true') && outcome.type === EOutcomeType.Redirect) {
                handleResetQuestionnaire();
                helpers?.setStatus(false);
            }
        }
        catch (error) {
            console.log(error);
        }
    };
    if (!questionnaire)
        return (_jsx(MainLayout, { children: _jsx(Box, { height: '100%', width: '100%', display: 'flex', justifyContent: 'center', children: _jsx(ErrorNotFound, { src: appImages['404'], content: 'We are really sorry but this page doesn’t exist any more. Why not go back home and see all the products we have to offer.', buttonContent: 'Home' }) }) }));
    if (status === 'error')
        return (_jsx(Box, { height: '100%', width: '100%', display: 'flex', children: _jsx(ErrorNotFound, { src: appImages['notFound'], content: "We're sorry, this page can <br/> not be found", center: false }) }));
    if (hasUnresolvedQuestion) {
        console.error(`Questionnaire ${questionnaire.id} has no resolvable question to display. ` +
            `startQuestionId=${questionnaire.startQuestionId}, storedQuestionId=${currentQuestionIdMerge}, ` +
            `questionCount=${questionnaire.questions?.length}. ` +
            `The questionnaire most likely has no start question set.`);
        return (_jsx(Box, { height: '100%', width: '100%', display: 'flex', children: _jsx(ErrorNotFound, { src: appImages['notFound'], content: "We're sorry, this questionnaire is not <br/> available at the moment", center: false }) }));
    }
    return (_jsxs(ThemeProvider, { appImages: appImages, googleKey: googleKey, isEmbed: isEmbed === 'true', hideHeader: questionnaire.hideHeader, dataTheme: questionnaire?.theme || {}, isLoading: isLoading, hasLocalStorage: hasLocalStorage, currency: currency, defaultPicture: defaultPicture, children: [_jsx(MainLayout, { disabled: activeStepMerge <= 1, handleBack: handleBack, isWhiteLabel: isWhiteLabel, showBackButton: true, children: _jsxs(Box, { width: '100%', children: [_jsx(FormContainer, { initialValues: { ...questionnaireStateMerge, questionFieldSubmissions: [] }, onSubmit: handleSubmit, children: ({ values, setValues, errors }) => {
                                React.useEffect(() => {
                                    if (Object.keys(questionnaireStateMerge).length) {
                                        const leadGenerationQuestion = (questionnaire.questions || []).find((q) => q.type === EQuestionType.LeadGeneration && q.id === currentQuestionIdMerge);
                                        if (leadGenerationQuestion) {
                                            const questionFieldSubmissionState = questionnaireStateMerge.questionFieldSubmissions;
                                            setValues({
                                                ...values,
                                                questionFieldSubmissions: leadGenerationQuestion
                                                    ? sortBy(leadGenerationQuestion.fields, 'sortOrder').map(({ type, id, checked, title }) => {
                                                        const contentType = questionFieldSubmissionState[type];
                                                        const contentId = questionFieldSubmissionState[id];
                                                        const contentCheckbox = type === EFieldType.Checkbox && checked ? 'true' : '';
                                                        const content = contentType || contentId || contentCheckbox || '';
                                                        const addressLocation = questionFieldSubmissionState['location'];
                                                        if (type === EFieldType.Address) {
                                                            return { fieldId: id, content, location: addressLocation };
                                                        }
                                                        const isDiscount = title === 'Discount Code' || title === 'Promo Code';
                                                        if (type === EFieldType.Discount ||
                                                            (type === EFieldType.TextBox && isDiscount)) {
                                                            return { fieldId: id, content: discountCode };
                                                        }
                                                        return { fieldId: id, content };
                                                    })
                                                    : []
                                            });
                                        }
                                    }
                                }, [questionnaire, currentQuestionIdMerge]);
                                React.useEffect(() => {
                                    if (!isEqual(questionnaireStateMerge.answerSelectionIds, values.answerSelectionIds)) {
                                        setValues({
                                            ...values,
                                            answerSelectionIds: questionnaireStateMerge.answerSelectionIds,
                                            fieldAnswer: questionnaireStateMerge.fieldAnswer,
                                            questionFieldSubmissions: []
                                        });
                                    }
                                }, [questionnaireStateMerge.answerSelectionIds]);
                                return (!!currentQuestion &&
                                    !isLoadingData && (_jsxs(Box, { display: 'flex', flexDirection: 'column', width: '100%', height: { xs: '95%', sm: '100%' }, mb: 2, children: [!(isEmbed && questionnaire.hideProgressBar) && (_jsx(QuestionnaireProgress, { total: total, step: activeStepMerge })), _jsx(CSSTransition, { in: isShowToast, appear: true, classNames: 'fade', unmountOnExit: true, timeout: 500, children: _jsx(Box, { textAlign: 'center', bgcolor: colors.white, width: { xs: '90%', sm: '72%', md: '58%', lg: '65%', xl: '60%' }, mx: 'auto', p: 2, borderRadius: '0.3rem', className: classes.transitionBanner, children: _jsx(Typography, { variant: 'subtitle2', children: "Hi there, ready to finish? To make it easier, we have put you right back where you left off." }) }) }), currentQuestion.type !== EQuestionType.LeadGeneration && (_jsx(QuestionElement, { question: currentQuestion.title, suggestion: currentQuestion.helperText })), _jsxs(Box, { flexGrow: 1, display: 'flex', px: 2, mb: 2, gridGap: '0.5rem', children: [_jsx(BackButton, { disabled: activeStepMerge <= 1, handleBack: handleBack }), currentQuestion.type !== EQuestionType.LeadGeneration && !isFieldQuestion && (_jsx(QuestionLayout, { questionnaireId: id, currentQuestion: currentQuestion, handleSelectAnswer: handleNext, questionnaireState: questionnaireStateMerge, setQuestionnaireStateMerge: setQuestionnaireStateMerge, trackMultiAnswerEvent: trackMultiAnswerEvent })), currentQuestion.type === EQuestionType.LeadGeneration && (_jsx(LeadGenerationLayout, { fields: currentQuestion.fields, handleSelectAnswer: handleNext, buttonSubmitText: currentQuestion.buttonSubmitText, handleChangeFields: handleChangeFields, questionLocation: dataPolygons, isMapEnabled: isMapEnabled, title: currentQuestion.title })), isFieldQuestion && (_jsx(FieldQuestionLayout, { currentQuestion: currentQuestion, handleSelectAnswer: handleNext, setQuestionnaireStateMerge: setQuestionnaireStateMerge, questionnaireState: questionnaireStateMerge }))] })] })));
                            } }), isLoadingData && _jsx(LoadingDiv, { open: isLoadingData })] }) }), _jsx(CookieSettingsLink, { questionnaireName: questionnaire?.name, themeName: questionnaire?.theme?.title })] }));
};
const useStyles = makeStyles({
    transitionBanner: {
        '&.fade-exit': {
            opacity: 1,
            height: 'max-content'
        },
        '&.fade-exit, &.fade-exit-active': {
            opacity: 0.01,
            height: 0,
            padding: 0,
            overflow: 'hidden',
            transition: 'all 500ms ease-in'
        }
    }
});
